import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { formatDate, formatRole } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Search,
  Shield,
  Mail,
  Phone,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Loader2,
  AlertTriangle,
  DollarSign,
  Eye,
  Building2
} from 'lucide-react';
import PayRatesTab from '../components/PayRatesTab';
import PersonnelCompliancePage from "./PersonnelCompliancePage";
import UserLicencesTab from '../components/UserLicencesTab';
import OfficerAvatar from '@/components/common/OfficerAvatar';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', level: 5 },
  { value: 'operations_admin', label: 'Operations Admin', level: 4 },
  { value: 'supervisor', label: 'Supervisor', level: 3 },
  { value: 'security_officer', label: 'Security Officer', level: 2 },
  { value: 'contractor_admin', label: 'Contractor Administrator', level: 2 },
  { value: 'client_viewer', label: 'Client Viewer', level: 1 }
];

const ENGAGEMENT_TYPES = [
  { value: 'bsms_full_time', label: 'BSMS Full-Time' },
  { value: 'bsms_part_time', label: 'BSMS Part-Time' },
  { value: 'bsms_casual', label: 'BSMS Casual' },
  { value: 'contractor', label: 'Contractor' }
];

const getEngagementType = (user) => {
  if (user?.engagement_type) {
    return user.engagement_type;
  }

  if (user?.employee_type === 'contractor') {
    return 'contractor';
  }

  if (user?.employee_type === 'casual') {
    return 'bsms_casual';
  }

  return '';
};

const getEngagementLabel = (user) => {
  const value = getEngagementType(user);

  if (!value) {
    return 'Requires Classification';
  }

  return (
    ENGAGEMENT_TYPES.find(
      (item) => item.value === value
    )?.label || 'Requires Classification'
  );
};

const ROLE_COLORS = {
  super_admin: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  operations_admin: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  supervisor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  security_officer: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  contractor_admin: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  client_viewer: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'
};

const ENGAGEMENT_TYPE_COLORS = {
  bsms_full_time: 'bg-emerald-500/20 text-emerald-400',
  bsms_part_time: 'bg-blue-500/20 text-blue-400',
  bsms_casual: 'bg-cyan-500/20 text-cyan-400',
  contractor: 'bg-orange-500/20 text-orange-400',
  unclassified: 'bg-yellow-500/20 text-yellow-400'
};

const ONBOARDING_STATUS = {
  active: {
    label: 'Active',
    className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
  },
  invitation_sent: {
    label: 'Invitation Sent',
    className: 'bg-amber-500/15 text-amber-400 border-amber-500/30'
  },
  invitation_pending: {
    label: 'Invitation Pending',
    className: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
  },
  invitation_expired: {
    label: 'Invitation Expired',
    className: 'bg-orange-500/15 text-orange-400 border-orange-500/30'
  },
  invitation_cancelled: {
    label: 'Invitation Cancelled',
    className: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30'
  },
  disabled: {
    label: 'Disabled',
    className: 'bg-red-500/15 text-red-400 border-red-500/30'
  }
};

const getOnboardingStatus = (user) =>
  ONBOARDING_STATUS[user?.onboarding_status] ||
  ONBOARDING_STATUS.active;

const getDeploymentReadiness = (user) => {
  const complianceScore = Number(
    user?.compliance_score ??
    user?.compliance_summary?.score ??
    0
  );

  const expired = Number(
    user?.expired_count ??
    user?.compliance_summary?.expired ??
    0
  );

  const critical = Number(
    user?.critical_count ??
    user?.compliance_summary?.critical ??
    0
  );

  const missing = Number(
    user?.missing_count ??
    user?.compliance_summary?.missing ??
    0
  );

  const deployable =
    user?.deployable === true ||
    user?.compliance_summary?.deployable === true ||
    (
      complianceScore >= 100 &&
      expired === 0 &&
      critical === 0 &&
      missing === 0
    );

  const reasons = [];

  if (expired > 0) {
    reasons.push(`${expired} expired compliance item${expired === 1 ? '' : 's'}`);
  }

  if (critical > 0) {
    reasons.push(`${critical} critical compliance issue${critical === 1 ? '' : 's'}`);
  }

  if (missing > 0) {
    reasons.push(`${missing} missing requirement${missing === 1 ? '' : 's'}`);
  }

  if (!deployable && reasons.length === 0) {
    reasons.push('Compliance profile requires review');
  }

  return {
    deployable,
    complianceScore,
    reasons,
    title: deployable
      ? 'Ready for Rostering'
      : 'Not Ready for Rostering',
    description: deployable
      ? 'All mandatory compliance requirements are current.'
      : reasons.join(' · ')
  };
};



export default function UsersPage() {
  const { api, isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [adminContractors, setAdminContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showTimelineDialog, setShowTimelineDialog] = useState(false);
  const [timelineUser, setTimelineUser] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [profileComplianceSummary, setProfileComplianceSummary] = useState(null);
  const [showContractorDialog, setShowContractorDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountSetupMode, setAccountSetupMode] = useState('invite');
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);

  const [createFormData, setCreateFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'security_officer',
    engagement_type: '',
    contractor_profile_id: '',
    contractor_id: ''
  });

  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    role: '',
    engagement_type: '',
    contractor_profile_id: '',
    is_active: true,
    // Compliance fields
    employee_id: '',
    driver_licence_number: '',
    driver_licence_expiry: '',
    security_licence_number: '',
    security_licence_expiry: '',
    first_aid_number: '',
    first_aid_expiry: ''
  });

  const [newContractor, setNewContractor] = useState({
    company_name: '',
    trading_name: '',
    abn: '',
    primary_contact: '',
    phone: '',
    email: ''
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/users';
      if (roleFilter && roleFilter !== 'all') url += `?role=${roleFilter}`;
      const [usersResponse, contractorsResponse] = await Promise.all([
        api.get(url),
        api.get('/contractors/active')
      ]);
      setUsers(usersResponse.data);
      setContractors(contractorsResponse.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [api, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    let cancelled = false;

    const fetchAdminContractors = async () => {
      try {
        const response = await api.get(
          '/contractor-onboarding/setup/available-contractors'
        );

        const records = Array.isArray(response.data?.contractors)
          ? response.data.contractors
          : Array.isArray(response.data)
            ? response.data
            : [];

        if (!cancelled) {
          setAdminContractors(
            records.filter(
              (item) =>
                item?.onboarding_contractor_id &&
                item?.status !== 'inactive'
            )
          );
        }
      } catch (error) {
        console.error(
          'Failed to load contractor administrator organisations:',
          error
        );

        if (!cancelled) {
          setAdminContractors([]);
        }
      }
    };

    fetchAdminContractors();

    return () => {
      cancelled = true;
    };
  }, [api]);

  const resetCreateForm = () => {
    setCreateFormData({
      email: '',
      password: '',
    confirm_password: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: 'security_officer',
      engagement_type: '',
      contractor_profile_id: '',
      contractor_id: ''
    });
    setAccountSetupMode('invite');
  };

  const handleCreateContractor = async (e) => {
    e.preventDefault();
    if (accountSetupMode === 'manual') {

      if (!createFormData.password) {
        toast.error('Temporary password is required');
        return;
      }

      if (createFormData.password.length < 8) {
        toast.error('Temporary password must be at least 8 characters');
        return;
      }

      if (createFormData.password !== createFormData.confirm_password) {
        toast.error('Passwords do not match');
        return;
      }

    }

    setIsSubmitting(true);
    
    try {
      const response = await api.post('/contractors', newContractor);
      toast.success(`Contractor "${response.data.company_name}" created`);
      setContractors([...contractors, response.data]);
      setCreateFormData({ ...createFormData, contractor_profile_id: response.data.id });
      setShowContractorDialog(false);
      setNewContractor({
        company_name: '',
        trading_name: '',
        abn: '',
        primary_contact: '',
        phone: '',
        email: ''
      });
    } catch (error) {
      console.error('Error creating contractor:', error);
      toast.error(error.response?.data?.detail || 'Failed to create contractor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileImageUpload = async (file) => {
    if (!viewingUser?.id || !file) return;

    try {
      setUploadingProfileImage(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/users/${viewingUser.id}/profile-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const imageUrl =
        response?.data?.profile_image ||
        response?.data?.url;

      setViewingUser(prev => ({
        ...prev,
        profile_image: imageUrl
      }));

      setUsers(prev =>
        prev.map(u =>
          u.id === viewingUser.id
            ? { ...u, profile_image: imageUrl }
            : u
        )
      );

      toast.success('Profile image uploaded');
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail ||
        'Failed to upload profile image'
      );
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    const isContractorAdmin =
      createFormData.role === 'contractor_admin';

    if (
      !isContractorAdmin &&
      !createFormData.engagement_type
    ) {
      toast.error('Please select an engagement type');
      return;
    }

    if (
      !isContractorAdmin &&
      createFormData.engagement_type === 'contractor' &&
      !createFormData.contractor_profile_id
    ) {
      toast.error('Please select a contractor company');
      return;
    }

    if (
      isContractorAdmin &&
      !createFormData.contractor_id
    ) {
      toast.error(
        'Please select the contractor organisation this administrator manages'
      );
      return;
    }
    
    if (editFormData.new_password) {
      if (editFormData.new_password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }

      if (editFormData.new_password !== editFormData.confirm_password) {
        toast.error('Passwords do not match');
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      const { confirm_password, ...createData } = createFormData;

      const payload = {
        ...createData,
        password:
          accountSetupMode === 'manual'
            ? createFormData.password
            : null,
        send_invitation: accountSetupMode === 'invite',
        must_change_password: accountSetupMode === 'manual',

        engagement_type:
          isContractorAdmin
            ? null
            : createFormData.engagement_type,

        employee_type:
          isContractorAdmin
            ? 'employee'
            : undefined,

        contractor_id:
          isContractorAdmin
            ? createFormData.contractor_id
            : null,

        contractor_profile_id:
          !isContractorAdmin &&
          createFormData.engagement_type === 'contractor'
            ? createFormData.contractor_profile_id
            : null
      };

      const response = await api.post('/users', payload);

      toast.success(
        accountSetupMode === 'invite'
          ? `Secure invitation sent to ${response.data.email}`
          : `User "${response.data.first_name} ${response.data.last_name}" created with a temporary password`
      );
      setShowCreateDialog(false);
      resetCreateForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendInvitation = async (user) => {
    try {
      setIsSubmitting(true);

      const response = await api.post(
        `/users/${user.id}/resend-invitation`
      );

      toast.success(
        response.data?.email_sent
          ? `Invitation resent to ${user.email}`
          : 'Invitation renewed, but email delivery was not confirmed'
      );

      await fetchUsers();
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      toast.error(
        error.response?.data?.detail ||
        'Failed to resend invitation'
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleCancelInvitation = async (user) => {
    const confirmed = window.confirm(
      `Cancel the invitation for ${user.email}?`
    );

    if (!confirmed) return;

    try {
      setIsSubmitting(true);

      await api.post(
        `/users/${user.id}/cancel-invitation`
      );

      toast.success('Invitation cancelled');
      await fetchUsers();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      toast.error(
        error.response?.data?.detail ||
        'Failed to cancel invitation'
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleSendPasswordReset = async (user) => {
    const confirmed = window.confirm(
      `Send a password reset email to ${user.email}?`
    );

    if (!confirmed) return;

    try {
      setIsSubmitting(true);

      const response = await api.post(
        `/users/${user.id}/send-password-reset`
      );

      toast.success(
        response.data?.email_sent
          ? `Password reset email sent to ${user.email}`
          : 'Reset link created, but email delivery was not confirmed'
      );
    } catch (error) {
      console.error('Failed to send password reset:', error);
      toast.error(
        error.response?.data?.detail ||
        'Failed to send password reset'
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  useEffect(() => {
    const loadProfileComplianceSummary = async () => {
      const userId = viewingUser?.id || viewingUser?._id;

      if (!showDetailsDialog || !userId) {
        return;
      }

      try {
        setProfileComplianceSummary(null);

        const response = await api.get(
          `/personnel-compliance/user/${userId}`
        );

        const payload = response.data || {};

        setProfileComplianceSummary(
          Array.isArray(payload)
            ? null
            : payload.summary || null
        );
      } catch (error) {
        console.error(
          'Failed to load workforce compliance summary:',
          error
        );

        setProfileComplianceSummary(null);
      }
    };

    loadProfileComplianceSummary();
  }, [
    api,
    showDetailsDialog,
    viewingUser?.id,
    viewingUser?._id
  ]);


  const openTimelineDialog = async (user) => {
    setTimelineUser(user);
    setTimelineEvents([]);
    setShowTimelineDialog(true);
    setTimelineLoading(true);

    try {
      const response = await api.get(
        `/users/${user.id}/timeline`
      );

      setTimelineUser({
        ...user,
        ...(response.data?.user || {})
      });

      setTimelineEvents(response.data?.events || []);
    } catch (error) {
      console.error('Failed to load user timeline:', error);
      toast.error(
        error.response?.data?.detail ||
        'Failed to load account timeline'
      );
    } finally {
      setTimelineLoading(false);
    }
  };

  const formatTimelineDate = (value) => {
    if (!value) return 'Date unavailable';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return 'Date unavailable';
    }

    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTimelineDotClass = (severity) => {
    switch (severity) {
      case 'success':
        return 'bg-emerald-400 ring-emerald-500/20';
      case 'warning':
        return 'bg-amber-400 ring-amber-500/20';
      case 'danger':
        return 'bg-red-400 ring-red-500/20';
      default:
        return 'bg-blue-400 ring-blue-500/20';
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      role: user.role || 'security_officer',
      engagement_type: getEngagementType(user),
      contractor_profile_id: user.contractor_profile_id || '',
      is_active: user.is_active !== false,
      // Compliance fields
      employee_id: user.employee_id || '',
      driver_licence_number: user.driver_licence_number || '',
      driver_licence_expiry: user.driver_licence_expiry ? user.driver_licence_expiry.split('T')[0] : '',
      security_licence_number: user.security_licence_number || '',
      security_licence_expiry: user.security_licence_expiry ? user.security_licence_expiry.split('T')[0] : '',
      first_aid_number: user.first_aid_number || '',
      first_aid_expiry: user.first_aid_expiry ? user.first_aid_expiry.split('T')[0] : '',
      new_password: '',
      confirm_password: ''
    });
    setShowEditDialog(true);
  };

  const openDetailsDialog = (user) => {
    setViewingUser(user);
    setShowDetailsDialog(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    if (!editFormData.engagement_type) {
      toast.error('Please select an engagement type');
      return;
    }

    if (
      editFormData.engagement_type === 'contractor' &&
      !editFormData.contractor_profile_id
    ) {
      toast.error('Please select a contractor company');
      return;
    }

    // Guardrail: Prevent downgrading own role
    if (editingUser.id === currentUser?.id) {
      const currentRoleLevel = ROLES.find(r => r.value === currentUser.role)?.level || 0;
      const newRoleLevel = ROLES.find(r => r.value === editFormData.role)?.level || 0;
      if (newRoleLevel < currentRoleLevel) {
        toast.error('You cannot downgrade your own role');
        return;
      }
    }

    if (editFormData.new_password) {
      if (editFormData.new_password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }

      if (editFormData.new_password !== editFormData.confirm_password) {
        toast.error('Passwords do not match');
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        first_name: editFormData.first_name.trim(),
        last_name: editFormData.last_name.trim(),
        phone: editFormData.phone.trim() || null,
        role: editFormData.role,
        engagement_type: editFormData.engagement_type,
        contractor_profile_id: editFormData.engagement_type === 'contractor' 
          ? editFormData.contractor_profile_id 
          : null,
        is_active: editFormData.is_active,
        // Compliance fields
        employee_id: editFormData.employee_id.trim() || null,
        driver_licence_number: editFormData.driver_licence_number.trim() || null,
        driver_licence_expiry: editFormData.driver_licence_expiry || null,
        security_licence_number: editFormData.security_licence_number.trim() || null,
        security_licence_expiry: editFormData.security_licence_expiry || null,
        first_aid_number: editFormData.first_aid_number.trim() || null,
        first_aid_expiry: editFormData.first_aid_expiry || null,
        password: editFormData.new_password?.trim() || undefined
      };

      const response = await api.put(`/users/${editingUser.id}`, payload);
      toast.success(`User "${response.data.first_name} ${response.data.last_name}" updated`);
      setShowEditDialog(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete "${userName}"?`)) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success(`User "${userName}" deleted`);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (userId, isActive, userName) => {
    try {
      await api.put(`/users/${userId}`, { is_active: !isActive });
      toast.success(`User "${userName}" ${isActive ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const filteredUsers = users.filter(user =>
    user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if current user can change roles (admin/super_admin only)
  const canChangeRoles = currentUser?.role === 'super_admin' || currentUser?.role === 'operations_admin';

  if (!isAdmin) {
  
  const handleResetPassword = async () => {
    if (!selectedUser?.id || !resetPassword) {
      toast.error('Enter a temporary password');
      return;
    }

    try {
      setResettingPassword(true);

      await api.post(
        `/admin/users/${selectedUser.id}/reset-password`,
        {
          new_password: resetPassword,
          force_change: true
        }
      );

      toast.success('Password reset successfully');
      setResetPassword('');
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail || 'Failed to reset password'
      );
    } finally {
      setResettingPassword(false);
    }
  };


  return (
      <div className="flex items-center justify-center h-full">
        <Card className="bg-[#18181b] border-[#3f3f46] p-8">
          <div className="text-center text-zinc-500">
            <Shield className="w-12 h-10 mx-auto mb-4 opacity-50" />
            <p>Access Denied</p>
            <p className="text-sm mt-2">Admin privileges required</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            USER MANAGEMENT
          </h1>
          <p className="text-sm text-zinc-500">{users.length} registered users</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetCreateForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-blue-500 hover:bg-blue-600 text-white font-rajdhani font-semibold rounded-sm shadow-[0_0_10px_rgba(59,130,246,0.3)]"
              data-testid="create-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              NEW USER
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-rajdhani text-xl">CREATE USER</DialogTitle>
              <DialogDescription className="text-zinc-500">
                Add a new user to the system.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">First Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={createFormData.first_name}
                    onChange={(e) => setCreateFormData({...createFormData, first_name: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46]"
                    required
                    disabled={isSubmitting}
                    data-testid="user-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Last Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={createFormData.last_name}
                    onChange={(e) => setCreateFormData({...createFormData, last_name: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46]"
                    required
                    disabled={isSubmitting}
                    data-testid="user-lastname-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Work Email</Label>
                <Input
                  type="email"
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({...createFormData, email: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                  required
                  disabled={isSubmitting}
                  data-testid="user-email-input"
                />
                <p className="text-xs text-zinc-500">
                  {accountSetupMode === 'invite'
                    ? 'The secure invitation will be sent to this address.'
                    : 'This email address will be used as the employee login.'}
                </p>
              </div>
              
              {accountSetupMode === 'manual' && (
                <div className="space-y-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">
                      Temporary Password <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      type="password"
                      value={createFormData.password}
                      onChange={(e) =>
                        setCreateFormData({
                          ...createFormData,
                          password: e.target.value
                        })
                      }
                      placeholder="Minimum 8 characters"
                      minLength={8}
                      required={accountSetupMode === 'manual'}
                      disabled={isSubmitting}
                      autoComplete="new-password"
                      className="bg-[#09090b] border-[#3f3f46]"
                      data-testid="create-user-password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">
                      Confirm Temporary Password <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      type="password"
                      value={createFormData.confirm_password || ''}
                      onChange={(e) =>
                        setCreateFormData({
                          ...createFormData,
                          confirm_password: e.target.value
                        })
                      }
                      placeholder="Repeat temporary password"
                      minLength={8}
                      required={accountSetupMode === 'manual'}
                      disabled={isSubmitting}
                      autoComplete="new-password"
                      className="bg-[#09090b] border-[#3f3f46]"
                      data-testid="create-user-confirm-password"
                    />
                  </div>

                  <p className="text-xs text-amber-400">
                    The employee will be required to replace this temporary password after signing in.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Phone</Label>
                  <Input
                    value={createFormData.phone}
                    onChange={(e) => setCreateFormData({...createFormData, phone: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46]"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Role <span className="text-red-400">*</span></Label>
                  <Select 
                    value={createFormData.role} 
                    onValueChange={(v) =>
                      setCreateFormData({
                        ...createFormData,
                        role: v,
                        engagement_type:
                          v === 'contractor_admin'
                            ? ''
                            : createFormData.engagement_type,
                        contractor_profile_id:
                          v === 'contractor_admin'
                            ? ''
                            : createFormData.contractor_profile_id,
                        contractor_id:
                          v === 'contractor_admin'
                            ? createFormData.contractor_id
                            : ''
                      })
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      {ROLES.map(role => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Account Setup */}
              <div className="space-y-3 rounded-lg border border-[#3f3f46] bg-[#09090b]/60 p-4">
                <div>
                  <Label className="text-zinc-200 font-medium">
                    How should this user access Rapid X?
                  </Label>
                  <p className="mt-1 text-xs text-zinc-500">
                    Choose the user's initial login method.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setAccountSetupMode('invite');
                    setCreateFormData({
                      ...createFormData,
                      password: '',
                      confirm_password: ''
                    });
                  }}
                  disabled={isSubmitting}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    accountSetupMode === 'invite'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-[#3f3f46] hover:border-zinc-500'
                  }`}
                  data-testid="account-setup-invite"
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={accountSetupMode === 'invite'}
                      onChange={() => {}}
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium text-zinc-100">
                        Send secure invitation email
                        <span className="ml-2 rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-400">
                          Recommended
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-zinc-500">
                        The employee receives an email invitation, verifies their account and creates their own password.
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setAccountSetupMode('manual')}
                  disabled={isSubmitting}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    accountSetupMode === 'manual'
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-[#3f3f46] hover:border-zinc-500'
                  }`}
                  data-testid="account-setup-manual"
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={accountSetupMode === 'manual'}
                      onChange={() => {}}
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium text-zinc-100">
                        Administrator Setup
                        <span className="ml-2 rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-400">
                          Manual
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-zinc-500">
                        Use only when onboarding someone in person. The employee must change the temporary password after signing in.
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {createFormData.role === 'contractor_admin' && (
                <div className="space-y-3 rounded-lg border border-orange-500/30 bg-orange-500/5 p-4">
                  <div>
                    <Label className="text-zinc-200">
                      Contractor Organisation <span className="text-red-400">*</span>
                    </Label>
                    <p className="mt-1 text-xs text-zinc-500">
                      Select the contractor organisation this administrator manages.
                    </p>
                  </div>

                  <Select
                    value={createFormData.contractor_id}
                    onValueChange={(v) =>
                      setCreateFormData({
                        ...createFormData,
                        contractor_id: v
                      })
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger
                      className="bg-[#09090b] border-[#3f3f46]"
                      data-testid="contractor-admin-organisation-select"
                    >
                      <Building2 className="w-4 h-4 mr-2 text-orange-400" />
                      <SelectValue placeholder="Select contractor organisation..." />
                    </SelectTrigger>

                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      {adminContractors.map((contractor) => (
                        <SelectItem
                          key={contractor.onboarding_contractor_id}
                          value={contractor.onboarding_contractor_id}
                        >
                          {contractor.company_name ||
                            contractor.trading_name ||
                            contractor.legal_name ||
                            'Contractor'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <p className="text-xs text-zinc-500">
                    Portal access will be restricted to this organisation and its contractor workforce.
                  </p>

                  {adminContractors.length === 0 && (
                    <p className="text-xs text-amber-400">
                      No configured contractor organisations are available.
                    </p>
                  )}
                </div>
              )}

              {createFormData.role !== 'contractor_admin' && (
                <div className="space-y-2">
                <Label className="text-zinc-300">Engagement <span className="text-red-400">*</span></Label>
                <Select 
                  value={createFormData.engagement_type} 
                  onValueChange={(v) => setCreateFormData({
                  ...createFormData,
                  engagement_type: v,
                  contractor_profile_id:
                    v !== 'contractor'
                      ? ''
                      : createFormData.contractor_profile_id
                })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="user-engagement-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {ENGAGEMENT_TYPES.map(et => (
                      <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">Employment/payroll classification. Role controls system access separately.</p>
                </div>
              )}

              {/* Contractor Selection - Only show for contractor engagement */}
              {createFormData.role !== 'contractor_admin' &&
               createFormData.engagement_type === 'contractor' && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Contractor Company <span className="text-red-400">*</span></Label>
                  <div className="flex gap-2">
                    <Select 
                      value={createFormData.contractor_profile_id} 
                      onValueChange={(v) => setCreateFormData({...createFormData, contractor_profile_id: v})}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="bg-[#09090b] border-[#3f3f46] flex-1" data-testid="contractor-select">
                        <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
                        <SelectValue placeholder="Select contractor..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                        {contractors.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.company_name} {c.trading_name && `(${c.trading_name})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button"
                      variant="outline"
                      className="border-zinc-600"
                      onClick={() => setShowContractorDialog(true)}
                      disabled={isSubmitting}
                      data-testid="add-contractor-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {contractors.length === 0 && (
                    <p className="text-xs text-yellow-400">No contractors found. Click + to add a new contractor.</p>
                  )}
                </div>
              )}
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreateDialog(false)}
                  className="border-zinc-600"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-500 hover:bg-blue-600"
                  disabled={isSubmitting}
                  data-testid="submit-user-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {accountSetupMode === 'invite' ? 'Sending Invitation...' : 'Creating...'}
                    </>
                  ) : (
                    accountSetupMode === 'invite' ? 'Send Invitation' : 'Create User'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Workforce V1.1 Account Timeline */}
      <Dialog
        open={showTimelineDialog}
        onOpenChange={setShowTimelineDialog}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl">
              Account Timeline
            </DialogTitle>

            <DialogDescription className="text-zinc-400">
              {timelineUser
                ? `${timelineUser.first_name || ''} ${timelineUser.last_name || ''} · ${timelineUser.email || ''}`
                : 'User account activity'}
            </DialogDescription>
          </DialogHeader>

          {timelineUser && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-[#3f3f46] bg-[#09090b]/60 p-3 text-xs">
              <div>
                <div className="text-zinc-500">Role</div>
                <div className="mt-1 text-zinc-200">
                  {formatRole(timelineUser.role)}
                </div>
              </div>

              <div>
                <div className="text-zinc-500">Account Status</div>
                <div className="mt-1">
                  {(() => {
                    const lifecycle = getOnboardingStatus(timelineUser);
                    return (
                      <Badge className={`${lifecycle.className} border text-xs`}>
                        {lifecycle.label}
                      </Badge>
                    );
                  })()}
                </div>
              </div>

              <div>
                <div className="text-zinc-500">Failed Login Attempts</div>
                <div className="mt-1 text-zinc-200">
                  {timelineUser.failed_login_attempts || 0}
                </div>
              </div>

              <div>
                <div className="text-zinc-500">Account Locked</div>
                <div className="mt-1 text-zinc-200">
                  {timelineUser.account_locked ? 'Yes' : 'No'}
                </div>
              </div>
            </div>
          )}

          <div className="max-h-[55vh] overflow-y-auto pr-2">
            {timelineLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-7 w-7 animate-spin text-blue-400" />
              </div>
            ) : timelineEvents.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#3f3f46] p-8 text-center text-sm text-zinc-500">
                No account lifecycle events are available yet.
              </div>
            ) : (
              <div className="relative ml-3 space-y-0 border-l border-[#3f3f46]">
                {timelineEvents.map((event, index) => (
                  <div
                    key={`${event.event_type}-${event.timestamp}-${index}`}
                    className="relative pb-6 pl-7"
                  >
                    <span
                      className={`absolute -left-[7px] top-1 h-3.5 w-3.5 rounded-full ring-4 ${getTimelineDotClass(event.severity)}`}
                    />

                    <div className="rounded-lg border border-[#3f3f46] bg-[#09090b]/60 p-3">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                        <div className="text-sm font-medium text-zinc-100">
                          {event.title}
                        </div>

                        <div className="text-xs text-zinc-500">
                          {formatTimelineDate(event.timestamp)}
                        </div>
                      </div>

                      {event.description && (
                        <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                          {event.description}
                        </p>
                      )}

                      {event.actor_email && (
                        <div className="mt-2 text-[11px] text-zinc-500">
                          Actioned by {event.actor_email}
                        </div>
                      )}

                      {event.metadata?.ip_address && (
                        <div className="mt-1 text-[11px] font-mono text-zinc-600">
                          IP: {event.metadata.ip_address}
                        </div>
                      )}

                      {event.metadata?.expires_at && (
                        <div className="mt-1 text-[11px] text-amber-500">
                          Expires: {formatTimelineDate(event.metadata.expires_at)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-zinc-600"
              onClick={() => setShowTimelineDialog(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#09090b] border-[#3f3f46] text-white"
                data-testid="user-search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px] bg-[#09090b] border-[#3f3f46]">
                <Shield className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                <SelectItem value="all">All Roles</SelectItem>
                {ROLES.map(role => (
                  <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="border-b border-[#3f3f46] bg-[#18181b]/50 py-2 px-4">
          <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400" />
            USERS ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#3f3f46] bg-[#27272a]/50">
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Name</th>
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Email</th>
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Type</th>
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Role</th>
                    <th className="text-center p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Status</th>
                    <th className="text-right p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#3f3f46]">
                  {filteredUsers.map((user) => (
                    <tr 
                      key={user.id} 
                      className="hover:bg-[#27272a]/50 transition-colors"
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <OfficerAvatar user={user} size="sm" />
                          <span className="text-sm text-zinc-200">
                            {user.first_name} {user.last_name}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-zinc-400 font-mono">{user.email}</span>
                      </td>
                      <td className="p-3">
                        {(() => {
                          const engagementType =
                            getEngagementType(user) || 'unclassified';

                          return (
                            <Badge
                              className={`${ENGAGEMENT_TYPE_COLORS[engagementType] || ENGAGEMENT_TYPE_COLORS.unclassified} text-xs`}
                            >
                              {getEngagementLabel(user)}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="p-3">
                        <Badge className={`${ROLE_COLORS[user.role]} text-xs border`}>
                          {formatRole(user.role)}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        {(() => {
                          const lifecycle = getOnboardingStatus(user);

                          return (
                            <Badge className={`${lifecycle.className} text-xs border`}>
                              {lifecycle.label}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* View Details Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-blue-400"
                            onClick={() => openDetailsDialog(user)}
                            title="View details & pay rates"
                            data-testid={`view-user-${user.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {/* Account Timeline Button */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-violet-400 hover:bg-violet-500/10 hover:text-violet-300"
                            onClick={() => openTimelineDialog(user)}
                            title="View account timeline"
                            data-testid={`timeline-user-${user.id}`}
                          >
                            Timeline
                          </Button>

                          {/* Edit Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                            onClick={() => openEditDialog(user)}
                            title="Edit user"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          
                          {/* Workforce V1.0 Lifecycle Actions */}
                          {[
                            'invitation_sent',
                            'invitation_pending',
                            'invitation_expired',
                            'invitation_cancelled'
                          ].includes(user.onboarding_status) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs text-amber-400 hover:text-amber-300"
                              onClick={() => handleResendInvitation(user)}
                              disabled={isSubmitting}
                              title="Resend invitation"
                              data-testid={`resend-invitation-${user.id}`}
                            >
                              Resend
                            </Button>
                          )}

                          {[
                            'invitation_sent',
                            'invitation_pending',
                            'invitation_expired'
                          ].includes(user.onboarding_status) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs text-zinc-400 hover:text-red-400"
                              onClick={() => handleCancelInvitation(user)}
                              disabled={isSubmitting}
                              title="Cancel invitation"
                              data-testid={`cancel-invitation-${user.id}`}
                            >
                              Cancel
                            </Button>
                          )}

                          {user.onboarding_status === 'active' && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs text-blue-400 hover:text-blue-300"
                              onClick={() => handleSendPasswordReset(user)}
                              disabled={isSubmitting}
                              title="Send password reset"
                              data-testid={`password-reset-${user.id}`}
                            >
                              Reset
                            </Button>
                          )}

                          {/* Toggle Active Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                            onClick={() => handleToggleActive(user.id, user.is_active !== false, `${user.first_name} ${user.last_name}`)}
                            title={user.is_active !== false ? 'Deactivate' : 'Activate'}
                          >
                            {user.is_active !== false ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </Button>
                          
                          {/* Delete Button - Super Admin only, can't delete self */}
                          {currentUser?.role === 'super_admin' && user.id !== currentUser.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400"
                              onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) setEditingUser(null);
      }}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl">EDIT USER</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Update user details. Email cannot be changed.
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleEditUser} className="space-y-3">
              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label className="text-zinc-500">Email</Label>
                <Input
                  value={editingUser.email}
                  className="bg-[#09090b] border-[#3f3f46] text-zinc-500"
                  disabled
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">First Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={editFormData.first_name}
                    onChange={(e) => setEditFormData({...editFormData, first_name: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46]"
                    required
                    disabled={isSubmitting}
                    data-testid="edit-user-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Last Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={editFormData.last_name}
                    onChange={(e) => setEditFormData({...editFormData, last_name: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46]"
                    required
                    disabled={isSubmitting}
                    data-testid="edit-user-lastname"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Phone</Label>
                <Input
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                  disabled={isSubmitting}
                  data-testid="edit-user-phone"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Role</Label>
                  {canChangeRoles ? (
                    <Select 
                      value={editFormData.role} 
                      onValueChange={(v) => setEditFormData({...editFormData, role: v})}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="edit-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                        {ROLES.map(role => (
                          <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={formatRole(editFormData.role)}
                      className="bg-[#09090b] border-[#3f3f46] text-zinc-500"
                      disabled
                    />
                  )}
                  {!canChangeRoles && (
                    <p className="text-xs text-zinc-500">Only Super Admin can change roles</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Engagement</Label>
                  <Select 
                    value={editFormData.engagement_type} 
                    onValueChange={(v) => setEditFormData({
                    ...editFormData,
                    engagement_type: v,
                    contractor_profile_id:
                      v !== 'contractor'
                        ? ''
                        : editFormData.contractor_profile_id
                  })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="edit-user-engagement-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      {ENGAGEMENT_TYPES.map(et => (
                        <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contractor Selection for Edit - Only show for contractor engagement */}
              {editFormData.engagement_type === 'contractor' && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Contractor Company <span className="text-red-400">*</span></Label>
                  <Select 
                    value={editFormData.contractor_profile_id} 
                    onValueChange={(v) => setEditFormData({...editFormData, contractor_profile_id: v})}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="edit-contractor-select">
                      <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
                      <SelectValue placeholder="Select contractor..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      {contractors.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.company_name} {c.trading_name && `(${c.trading_name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}


              {/* Password Reset */}
              <div className="border-t border-[#3f3f46] pt-4 mt-4">
                <Label className="text-zinc-400 text-sm font-semibold mb-3 block">
                  PASSWORD RESET
                </Label>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">New Password</Label>
                    <Input
                      type="password"
                      value={editFormData.new_password || ''}
                      onChange={(e) => setEditFormData({...editFormData, new_password: e.target.value})}
                      className="bg-[#09090b] border-[#3f3f46]"
                      placeholder="Leave blank to keep existing"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Confirm Password</Label>
                    <Input
                      type="password"
                      value={editFormData.confirm_password || ''}
                      onChange={(e) => setEditFormData({...editFormData, confirm_password: e.target.value})}
                      className="bg-[#09090b] border-[#3f3f46]"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Status</Label>
                <Select 
                  value={editFormData.is_active ? 'active' : 'inactive'} 
                  onValueChange={(v) => setEditFormData({...editFormData, is_active: v === 'active'})}
                  disabled={isSubmitting || editingUser.id === currentUser?.id}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="edit-user-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                {editingUser.id === currentUser?.id && (
                  <p className="text-xs text-zinc-500">Cannot deactivate yourself</p>
                )}
              </div>

              {/* Compliance & Licensing Section */}
              <div className="border-t border-[#3f3f46] pt-4 mt-4">
                <Label className="text-zinc-400 text-sm font-semibold mb-3 block">COMPLIANCE & LICENSING</Label>
                
                <div className="space-y-3 max-h-[92vh] overflow-y-auto pr-2">
                  <div className="space-y-2">
                    <Label className="text-zinc-500 text-xs">Employee ID (unique)</Label>
                    <Input
                      value={editFormData.employee_id}
                      onChange={(e) => setEditFormData({...editFormData, employee_id: e.target.value})}
                      className="bg-[#09090b] border-[#3f3f46] h-9"
                      placeholder="e.g., EMP001"
                      disabled={isSubmitting}
                      data-testid="edit-employee-id"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-zinc-500 text-xs">Driver Licence #</Label>
                      <Input
                        value={editFormData.driver_licence_number}
                        onChange={(e) => setEditFormData({...editFormData, driver_licence_number: e.target.value})}
                        className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                        placeholder="Licence number"
                        disabled={isSubmitting}
                        data-testid="edit-driver-licence-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-500 text-xs">Driver Licence Expiry</Label>
                      <Input
                        type="date"
                        value={editFormData.driver_licence_expiry}
                        onChange={(e) => setEditFormData({...editFormData, driver_licence_expiry: e.target.value})}
                        className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                        disabled={isSubmitting}
                        data-testid="edit-driver-licence-expiry"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-zinc-500 text-xs">Security Licence #</Label>
                      <Input
                        value={editFormData.security_licence_number}
                        onChange={(e) => setEditFormData({...editFormData, security_licence_number: e.target.value})}
                        className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                        placeholder="Licence number"
                        disabled={isSubmitting}
                        data-testid="edit-security-licence-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-500 text-xs">Security Licence Expiry</Label>
                      <Input
                        type="date"
                        value={editFormData.security_licence_expiry}
                        onChange={(e) => setEditFormData({...editFormData, security_licence_expiry: e.target.value})}
                        className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                        disabled={isSubmitting}
                        data-testid="edit-security-licence-expiry"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-zinc-500 text-xs">First Aid Certificate #</Label>
                      <Input
                        value={editFormData.first_aid_number}
                        onChange={(e) => setEditFormData({...editFormData, first_aid_number: e.target.value})}
                        className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                        placeholder="Certificate number"
                        disabled={isSubmitting}
                        data-testid="edit-first-aid-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-500 text-xs">First Aid Expiry</Label>
                      <Input
                        type="date"
                        value={editFormData.first_aid_expiry}
                        onChange={(e) => setEditFormData({...editFormData, first_aid_expiry: e.target.value})}
                        className="bg-[#09090b] border-[#3f3f46] h-8 text-sm"
                        disabled={isSubmitting}
                        data-testid="edit-first-aid-expiry"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Warning for editing own role */}
              {editingUser.id === currentUser?.id && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-yellow-400">You are editing your own account</span>
                </div>
              )}
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditDialog(false)}
                  className="border-zinc-600"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-500 hover:bg-blue-600"
                  disabled={isSubmitting}
                  data-testid="submit-edit-user-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update User'
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* View Details Dialog with Pay Rates Tab */}
      <Dialog open={showDetailsDialog} onOpenChange={(open) => {
        setShowDetailsDialog(open);
        if (!open) setViewingUser(null);
      }}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white w-[96vw] max-w-7xl h-[96vh] overflow-hidden p-0">
          {viewingUser && (
            <div className="flex h-full">
              <aside className="w-[320px] shrink-0 border-r border-[#27272a] bg-[#111113] p-4 overflow-y-auto">
                <div className="flex flex-col items-center text-center">
                  <OfficerAvatar user={viewingUser} size="xl" className="mb-4" />
                  <h2 className="font-rajdhani text-2xl font-bold">
                    {viewingUser?.first_name} {viewingUser?.last_name}
                  </h2>
                  <p className="text-xs text-zinc-500 mt-1">Workforce Profile</p>

                  <div className="mt-4">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingProfileImage}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleProfileImageUpload(file);
                        }}
                      />

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingProfileImage}
                        className="w-full"
                        asChild
                      >
                        <span>
                          {uploadingProfileImage
                            ? 'Uploading...'
                            : 'Upload Profile Photo'}
                        </span>
                      </Button>
                    </label>
                  </div>

                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    <Badge className={`${ROLE_COLORS[viewingUser?.role]} text-xs border`}>
                      {formatRole(viewingUser?.role)}
                    </Badge>
                    <Badge
                      className={`${ENGAGEMENT_TYPE_COLORS[getEngagementType(viewingUser) || 'unclassified']} text-xs`}
                    >
                      {getEngagementLabel(viewingUser)}
                    </Badge>
                    <Badge className={`text-xs ${viewingUser.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                      {viewingUser.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="rounded-xl border border-[#27272a] bg-[#18181b] p-4">
                    <p className="text-xs uppercase tracking-wide text-zinc-500">Email</p>
                    <p className="text-sm text-zinc-200 font-mono break-all mt-1">{viewingUser.email || '-'}</p>
                  </div>
                  <div className="rounded-xl border border-[#27272a] bg-[#18181b] p-4">
                    <p className="text-xs uppercase tracking-wide text-zinc-500">Phone</p>
                    <p className="text-sm text-zinc-200 mt-1">{viewingUser.phone || '-'}</p>
                  </div>
                  <div className="rounded-xl border border-[#27272a] bg-[#18181b] p-4">
                    <p className="text-xs uppercase tracking-wide text-zinc-500">Created</p>
                    <p className="text-sm text-zinc-300 mt-1">{formatDate(viewingUser.created_at)}</p>
                  </div>
                  {(() => {
                    const summaryLoading =
                      profileComplianceSummary === null;

                    const readiness = getDeploymentReadiness({
                      ...viewingUser,
                      compliance_summary: profileComplianceSummary,
                      compliance_score: profileComplianceSummary?.score,
                      expired_count: profileComplianceSummary?.expired,
                      critical_count: profileComplianceSummary?.critical,
                      warning_count: profileComplianceSummary?.warning,
                      missing_count: profileComplianceSummary?.missing,
                      deployable: profileComplianceSummary?.deployable
                    });

                    return (
                      <div
                        className={`rounded-xl border p-4 ${
                          readiness.deployable
                            ? 'border-emerald-500/30 bg-emerald-500/10'
                            : 'border-red-500/30 bg-red-500/10'
                        }`}
                      >
                        <p
                          className={`text-xs uppercase tracking-wide ${
                            readiness.deployable
                              ? 'text-emerald-400'
                              : 'text-red-400'
                          }`}
                        >
                          Deployment Readiness
                        </p>

                        <p className="mt-1 text-base font-semibold text-zinc-100">
                          {summaryLoading
                            ? 'Checking Compliance'
                            : readiness.title}
                        </p>

                        <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                          {summaryLoading
                            ? 'Loading current licence and document records…'
                            : readiness.description}
                        </p>

                        <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3">
                          <span className="text-xs text-zinc-500">
                            Compliance Score
                          </span>

                          <span
                            className={`text-lg font-bold ${
                              readiness.deployable
                                ? 'text-emerald-400'
                                : 'text-red-400'
                            }`}
                          >
                            {summaryLoading
                              ? '—'
                              : `${readiness.complianceScore}%`}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </aside>

              <main className="flex-1 h-full overflow-y-auto p-4 pb-24">
                <DialogHeader className="mb-4">
                  <DialogTitle className="font-rajdhani text-2xl">
                    Workforce Profile
                  </DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    Manage personnel details, compliance records, licences and pay rates.
                  </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="details" className="mt-4 min-h-full flex flex-col">
              <TabsList className="bg-[#27272a] border border-[#3f3f46] h-10 p-1 gap-1">
                <TabsTrigger value="details" className="h-10 px-5 text-sm data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
                  Profile Details
                </TabsTrigger>
                <TabsTrigger 
                  value="licences" 
                  className="h-10 px-5 text-sm data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400"
                  data-testid="licences-tab-trigger"
                >
                  <Shield className="w-4 h-4 mr-1" />
                  Compliance & Licences
                </TabsTrigger>
                <TabsTrigger 
                  value="pay-rates" 
                  className="h-10 px-5 text-sm data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
                  data-testid="pay-rates-tab-trigger"
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Pay Rates
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="mt-6">
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                  <div className="xl:col-span-2 rounded-2xl border border-[#27272a] bg-[#111113] p-5">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h3 className="font-rajdhani text-xl font-semibold text-white">Identity & Contact</h3>
                        <p className="text-sm text-zinc-500">Core account and contact information for this workforce profile.</p>
                      </div>
                      <Badge className={`text-xs ${viewingUser.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                        {viewingUser.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-xl border border-[#27272a] bg-[#18181b] p-4">
                        <Label className="text-zinc-500 text-xs uppercase tracking-wide">Email Address</Label>
                        <p className="text-sm text-zinc-200 font-mono break-all mt-2">{viewingUser.email || '-'}</p>
                      </div>

                      <div className="rounded-xl border border-[#27272a] bg-[#18181b] p-4">
                        <Label className="text-zinc-500 text-xs uppercase tracking-wide">Phone Number</Label>
                        <p className="text-sm text-zinc-200 mt-2">{viewingUser.phone || '-'}</p>
                      </div>

                      <div className="rounded-xl border border-[#27272a] bg-[#18181b] p-4">
                        <Label className="text-zinc-500 text-xs uppercase tracking-wide">Role</Label>
                        <div className="mt-2">
                          <Badge className={`${ROLE_COLORS[viewingUser?.role]} text-xs border`}>
                            {formatRole(viewingUser?.role)}
                          </Badge>
                        </div>
                      </div>

                      <div className="rounded-xl border border-[#27272a] bg-[#18181b] p-4">
                        <Label className="text-zinc-500 text-xs uppercase tracking-wide">Engagement</Label>
                        <div className="mt-2">
                          <Badge
                            className={`${ENGAGEMENT_TYPE_COLORS[getEngagementType(viewingUser) || 'unclassified']} text-xs`}
                          >
                            {getEngagementLabel(viewingUser)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#27272a] bg-[#111113] p-5">
                    <h3 className="font-rajdhani text-xl font-semibold text-white mb-1">Operational Summary</h3>
                    <p className="text-sm text-zinc-500 mb-5">Quick profile health indicators.</p>

                    <div className="space-y-3">
                      <div className="rounded-xl border border-blue-500/20 bg-blue-500/10 p-4">
                        <p className="text-xs uppercase tracking-wide text-blue-400">Profile Created</p>
                        <p className="text-sm text-zinc-200 mt-1">{formatDate(viewingUser.created_at)}</p>
                      </div>

                      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
                        <p className="text-xs uppercase tracking-wide text-emerald-400">Account Status</p>
                        <p className="text-sm text-zinc-200 mt-1">{viewingUser.is_active !== false ? 'Active and available' : 'Inactive'}</p>
                      </div>

                      <div className="rounded-xl border border-orange-500/20 bg-orange-500/10 p-4">
                        <p className="text-xs uppercase tracking-wide text-orange-400">Compliance</p>
                        <p className="text-sm text-zinc-200 mt-1">Review licences and uploaded documents in the Compliance tab.</p>
                      </div>
                    </div>
                  </div>

                  {viewingUser.contractor_profile_id && (
                    <div className="xl:col-span-3 rounded-2xl border border-orange-500/20 bg-orange-500/10 p-5">
                      <Label className="text-orange-400 text-xs uppercase tracking-wide">Contractor Company</Label>
                      <p className="text-sm text-zinc-200 flex items-center gap-2 mt-2">
                        <Building2 className="w-4 h-4 text-orange-400" />
                        {contractors.find(c => c.id === viewingUser.contractor_profile_id)?.company_name || 'Unknown'}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="licences" className="mt-4">
                <PersonnelCompliancePage
                  userId={viewingUser?.id || viewingUser?._id}
                  onSummaryChange={setProfileComplianceSummary}
                />
              </TabsContent>
              
              <TabsContent value="pay-rates" className="mt-4">
                <PayRatesTab officer={viewingUser} onRateChange={fetchUsers} />
              </TabsContent>
                </Tabs>
              </main>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Contractor Dialog */}
      <Dialog open={showContractorDialog} onOpenChange={setShowContractorDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-400" />
              ADD CONTRACTOR
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Register a new contractor company.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateContractor} className="space-y-3">
            <div className="space-y-2">
              <Label className="text-zinc-300">Company Name <span className="text-red-400">*</span></Label>
              <Input
                value={newContractor.company_name}
                onChange={(e) => setNewContractor({...newContractor, company_name: e.target.value})}
                className="bg-[#09090b] border-[#3f3f46]"
                required
                disabled={isSubmitting}
                placeholder="Legal company name"
                data-testid="contractor-company-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Trading Name</Label>
                <Input
                  value={newContractor.trading_name}
                  onChange={(e) => setNewContractor({...newContractor, trading_name: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                  disabled={isSubmitting}
                  placeholder="DBA name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">ABN</Label>
                <Input
                  value={newContractor.abn}
                  onChange={(e) => setNewContractor({...newContractor, abn: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                  disabled={isSubmitting}
                  placeholder="XX XXX XXX XXX"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Primary Contact</Label>
              <Input
                value={newContractor.primary_contact}
                onChange={(e) => setNewContractor({...newContractor, primary_contact: e.target.value})}
                className="bg-[#09090b] border-[#3f3f46]"
                disabled={isSubmitting}
                placeholder="Contact person name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Phone</Label>
                <Input
                  value={newContractor.phone}
                  onChange={(e) => setNewContractor({...newContractor, phone: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={newContractor.email}
                  onChange={(e) => setNewContractor({...newContractor, email: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowContractorDialog(false)}
                className="border-zinc-600"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-orange-500 hover:bg-orange-600"
                disabled={isSubmitting}
                data-testid="submit-contractor-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Add Contractor'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
